import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import {useGetGenerateStatus} from '../../../hooks/productions/useProductions';
import {GetGenerateStatusRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const GenerateStatusApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [moduletype, setModuletype] = useState<string>('PRODUCTION');
  const [productionId, setProductionId] = useState<number>(216);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetGenerateStatusRequestParam = {
    SecurityGroupId: securityGroupId,
    moduletype: moduletype,
    productionId: productionId,
  };

  const {
    data: statusData,
    isLoading,
    error,
    refetch,
  } = useGetGenerateStatus(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (statusData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(statusData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'COMPLETE' || status === 'INPROGRESS') return 'success';
    if (status === 'FAILED' || status === 'ERROR') return 'error';
    return 'default';
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Generate Status API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get current production generation status with progress information and
          state details.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Generate Status
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/generate/status
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get current production generation status with status text, state,
            progress counts, and completion information.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduletype}
              onChange={(e) => setModuletype(e.target.value)}
              size="small"
              placeholder="e.g., PRODUCTION, EXPORT"
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="generate-status-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="generate-status-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="generate-status-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {statusData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="generate-status-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Fetching generate status...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while fetching generate status'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {statusData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Production Current Status
              </Typography>

              {/* Status Information */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Status Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Status Text:</strong>{' '}
                        {
                          statusData.productionCurrentStatus
                            .productionStatusText
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Status:</strong>{' '}
                        <Chip
                          label={
                            statusData.productionCurrentStatus.productionStatus
                          }
                          color={getStatusColor(
                            statusData.productionCurrentStatus.productionStatus
                          )}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production State:</strong>{' '}
                        <Chip
                          label={
                            statusData.productionCurrentStatus.productionState
                          }
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Status Code:</strong>{' '}
                        {
                          statusData.productionCurrentStatus
                            .productionStatusCode
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is Mark Complete:</strong>{' '}
                        {statusData.productionCurrentStatus.isMarkComplete
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Should Continue:</strong>{' '}
                        {statusData.productionCurrentStatus.shouldContinue
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Progress Information */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Progress Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Generated Bates Range:</strong>{' '}
                        {statusData.productionCurrentStatus.generatedBatesRange}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Total Doc Count:</strong>{' '}
                        {statusData.productionCurrentStatus.totalDocCount === -1
                          ? 'N/A'
                          : statusData.productionCurrentStatus.totalDocCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Completed Doc Count:</strong>{' '}
                        {statusData.productionCurrentStatus.completedDocCount}
                      </Typography>
                    </Grid>
                    {statusData.productionCurrentStatus
                      .producitonDocsComplete && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography className="typo-text body3_size">
                          <strong>Docs Complete Status:</strong>{' '}
                          {
                            statusData.productionCurrentStatus
                              .producitonDocsComplete
                          }
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(statusData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default GenerateStatusApiTest;
